import { useEffect, useState } from "react";
import { classFor, labelFor } from "@/lib/statuses";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, Camera } from "lucide-react";
import { ParcialFabDialog } from "@/components/ParcialFabDialog";

/**
 * Inline sub-status pill. Read-only when canEdit=false. Otherwise clickable dropdown that PATCHes /orders/{id}/subestado.
 */
export default function InlineStatus({
  orderId,
  campo,          // "estado_fabricacion" | "estado_empaque" | "estado_entrega" | "estado_facturacion"
  value,
  options,        // array from statuses.js
  canEdit = false,
  onChanged,      // optional callback(newValue)
  compact = false,
}) {
  const [current, setCurrent] = useState(value);
  const [photoDialog, setPhotoDialog] = useState(false);
  const [photoData, setPhotoData] = useState("");
  const [photoNota, setPhotoNota] = useState("");
  const [saving, setSaving] = useState(false);
  const [parcialOpen, setParcialOpen] = useState(false);

  // Sync when parent prop changes (after reload)
  useEffect(() => { setCurrent(value); }, [value]);

  const cls = classFor(options, current);
  const label = labelFor(options, current);

  const pill = (
    <span
      className={`inline-flex items-center gap-1 rounded-full ${cls} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        compact ? "" : "whitespace-nowrap"
      } ${canEdit ? "cursor-pointer hover:opacity-80" : ""}`}
      data-testid={`inline-${campo}-${orderId}`}
    >
      {label}
      {canEdit && <ChevronDown className="w-3 h-3 opacity-60" />}
    </span>
  );

  if (!canEdit) return pill;

  const requiresPhoto = (c, v) => c === "estado_fabricacion" && v === "listo";

  const patchSubestado = async (payload) => {
    await api.patch(`/orders/${orderId}/subestado`, payload);
  };

  const change = async (v) => {
    if (v === current) return;
    if (requiresPhoto(campo, v)) {
      setPhotoData("");
      setPhotoNota("");
      setPhotoDialog(true);
      return;
    }
    try {
      await patchSubestado({ campo, valor: v });
      setCurrent(v);
      onChanged?.(v);
      if (campo === "estado_fabricacion" && v === "parcial") setParcialOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFileSelected = (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("La foto es demasiado grande (máx 6 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhotoData(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const confirmFabricadoConFoto = async () => {
    if (!photoData) {
      toast.error("Sube una foto del pedido fabricado");
      return;
    }
    setSaving(true);
    try {
      await patchSubestado({ campo, valor: "listo", foto_base64: photoData, nota: photoNota });
      setCurrent("listo");
      onChanged?.("listo");
      setPhotoDialog(false);
      toast.success("Pedido marcado como fabricado con evidencia fotográfica");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{pill}</DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[180px]">
          {options.map((o) => (
            <DropdownMenuItem
              key={o.value}
              onClick={() => change(o.value)}
              data-testid={`opt-${campo}-${o.value}`}
              className={`text-xs ${o.value === current ? "font-semibold" : ""}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${o.cls.split(" ")[0]}`} />
              {o.label}
              {requiresPhoto(campo, o.value) && (
                <Camera className="w-3 h-3 ml-auto text-zinc-400" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={photoDialog} onOpenChange={setPhotoDialog}>
        <DialogContent data-testid="dialog-foto-fabricacion">
          <DialogHeader>
            <DialogTitle>Marcar como Fabricado</DialogTitle>
            <DialogDescription>
              Antes de marcar el pedido como <b>Listo</b>, es obligatorio subir una foto del pedido fabricado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-widest">Foto del pedido fabricado <span className="text-red-600">*</span></Label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => onFileSelected(e.target.files?.[0])}
                data-testid="input-foto-fabricacion"
                className="block w-full text-sm mt-1"
              />
              {photoData && (
                <img src={photoData} alt="Preview" className="mt-2 rounded-md border max-h-48 object-contain" />
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest">Nota (opcional)</Label>
              <Textarea
                rows={2}
                value={photoNota}
                onChange={(e) => setPhotoNota(e.target.value)}
                placeholder="Detalles de calidad, cantidad, observaciones..."
                data-testid="input-nota-fabricacion"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoDialog(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={confirmFabricadoConFoto} disabled={saving || !photoData} data-testid="btn-confirmar-fabricado">
              {saving ? "Guardando..." : "Confirmar y marcar fabricado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {parcialOpen && <ParcialFabDialog orderId={orderId} open={parcialOpen} onOpenChange={setParcialOpen} onSaved={() => onChanged?.("parcial")} />}
    </>
  );
}
